import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Settings, Monitor, Cpu, Code2, Globe, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "process-optimization",
    icon: Settings,
    title: "Prozessoptimierung",
    href: "/prozessoptimierung",
    description:
      "Analyse und Neugestaltung Ihrer Geschäftsprozesse für maximale Effizienz. Wir identifizieren Engpässe und implementieren bewährte Lean-Methoden.",
    features: [
      "Prozessanalyse & Mapping",
      "Lean Management",
      "Workflow-Automatisierung",
      "Change Management",
    ],
    span: "lg:col-span-7",
    height: "min-h-[400px]",
  },
  {
    id: "digitalization",
    icon: Monitor,
    title: "Digitalisierung",
    href: "/digitalisierung",
    description:
      "Entwicklung und Umsetzung massgeschneiderter Digitalstrategien. Von der Konzeption bis zur Implementierung begleiten wir Ihre digitale Transformation.",
    features: [
      "Digitalstrategie",
      "System-Integration",
      "Cloud Migration",
      "Digital Workplace",
    ],
    span: "lg:col-span-5",
    height: "min-h-[400px]",
  },
  {
    id: "ai-automation",
    icon: Cpu,
    title: "KI & Automatisierung",
    href: "/ki-beratung",
    description:
      "Integration von künstlicher Intelligenz und intelligenter Automatisierung in Ihre Geschäftsprozesse für nachhaltige Wettbewerbsvorteile.",
    features: [
      "Machine Learning",
      "Predictive Analytics",
      "RPA Implementation",
      "Chatbots & Assistenten",
    ],
    span: "lg:col-span-4",
    height: "min-h-[400px]",
  },
  {
    id: "software-development",
    icon: Code2,
    title: "Software Entwicklung",
    href: "/software-entwicklung",
    description:
      "Massgeschneiderte Softwarelösungen, individuell auf Ihre Geschäftsanforderungen zugeschnitten. Von der Konzeption bis zum Go-Live entwickeln wir Ihre digitalen Produkte.",
    features: [
      "Individuelle Entwicklung",
      "Business-Anpassung",
      "API-Integration",
      "Wartung & Support",
    ],
    span: "lg:col-span-4",
    height: "min-h-[400px]",
  },
  {
    id: "web-digital-marketing",
    icon: Globe,
    title: "Web & Digital Marketing",
    href: "/web-marketing",
    description:
      "Content, SEO, Social & Ads automatisieren. Wir entwickeln datengetriebene Marketingstrategien und setzen diese mit modernsten Tools effizient um.",
    features: [
      "SEO & Content",
      "Social Media Automation",
      "Paid Ads & Analytics",
      "Marketing Automation",
    ],
    efficiency: "20–40%",
    span: "lg:col-span-4",
    height: "min-h-[400px]",
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`${service.span} ${service.height} bg-white border border-lab-border p-8 lg:p-10 card-hover group relative overflow-hidden`}
      data-testid={`service-card-${service.id}`}
    >
      {/* Hover accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-lab-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

      <div className="flex flex-col h-full">
        <div>
          {/* Icon */}
          <div className="w-14 h-14 bg-lab-bg-light flex items-center justify-center mb-6">
            <Icon className="w-7 h-7 text-lab-accent" strokeWidth={1.5} />
          </div>

          {/* Title */}
          <h3 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4 flex items-center gap-3">
            {service.title}
            <ArrowUpRight className="w-5 h-5 text-lab-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </h3>

          {/* Description */}
          <p className="text-lab-text-muted leading-relaxed mb-6">
            {service.description}
          </p>
        </div>

        {/* Features */}
        <div className="mt-auto">
          <div className="grid grid-cols-2 gap-3">
            {service.features.map((feature, i) => (
              <div
                key={i}
                className="text-sm text-lab-text py-2 px-3 bg-lab-bg-light"
              >
                {feature}
              </div>
            ))}
          </div>
          <Link
            to={service.href}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-lab-accent mt-6 hover:gap-2.5 transition-all"
            data-testid={`service-link-${service.id}`}
          >
            Mehr erfahren <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      className="section-padding bg-lab-bg-light"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="label-text">Unsere Leistungen</span>
          <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4 max-w-2xl">
            Expertise für Ihre digitale Zukunft
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
